/**
 * Ordnerbaum für „Dokument aus Fallakte beifügen" im E-Mail-Fenster.
 *
 * Rückmeldung Kanzlei Polz 23.10.2026: Beim Beifügen von Anhängen aus der
 * Akte sollen die Dokumente – wie im Aktenbaum von RA-Micro – nach Ordnern
 * und Unterordnern sortiert erscheinen statt als flache Liste.
 *
 * Reine Funktionen ohne Vue: Die Ordner kommen aus
 * GET /cases/{id}/doc-folders/targets (key, parentKey, label), die
 * Dokumente aus GET /cases/{id}/documents. Ein Dokument gehört über seine
 * Kategorie zu einem Ordner (Standardordner-Klasse, Rechnungsart oder
 * "folder:<id>" für eigene Ordner) – dieselbe Zuordnung wie in der
 * Dokumentenansicht der Akte (SectionDokumente.classIdForCategory).
 */
import { gtKey } from '@/i18n/gt'
import { CATEGORY_TO_CLASS, INVOICE_KIND_TO_CLASS } from '@/pages/pageCases/pageCaseDetail/sections/components/docClassDefs'
import { buildTree, flattenTree, type TreeNode } from './folderTree'

export interface PickerFolder {
  key: string
  parentKey: string | null
  label: string
  path?: string
}

export interface PickerDocument {
  [extra: string]: unknown
  id: number
  category: string
  invoiceKind?: string | null
}

export interface PickerGroup<T extends PickerDocument = PickerDocument> {
  key: string
  label: string
  path: string
  depth: number
  parentKey: string | null
  ancestorKeys: string[]
  /** Dokumente direkt in diesem Ordner. */
  docs: T[]
  /** Dokumente in diesem Ordner und allen Unterordnern. */
  subtreeCount: number
}

const FOLDER_CATEGORY_PREFIX = 'folder:'
const FALLBACK_KEY = 'sonstiges'
/** Auffanggruppe, wenn die Akte keinen Ordner „Sonstiges" liefert. */
export const ORPHAN_GROUP_KEY = '__weitere'
const ORPHAN_GROUP_LABEL = gtKey('Weitere Dokumente')
const SINGLE_GROUP_LABEL = gtKey('Dokumente der Akte')

/**
 * Ordnerschlüssel eines Dokuments: eigener Ordner ("folder:<id>", sofern
 * bekannt), Rechnung nach Rechnungsart, sonst Klasse der Kategorie –
 * Rückfall „sonstiges".
 */
export function folderKeyForDocument (doc: PickerDocument, knownKeys: ReadonlySet<string>): string {
  const category = String(doc.category || '')
  if (category.startsWith(FOLDER_CATEGORY_PREFIX)) {
    return knownKeys.has(category) ? category : FALLBACK_KEY
  }
  const byKind = category === 'rechnung' && doc.invoiceKind ? INVOICE_KIND_TO_CLASS[doc.invoiceKind] : undefined
  if (byKind && knownKeys.has(byKind)) {
    return byKind
  }
  const cls = CATEGORY_TO_CLASS[category]
  return cls && knownKeys.has(cls) ? cls : FALLBACK_KEY
}

const FOLDER_TREE = {
  keyOf: (f: PickerFolder) => f.key,
  parentKeyOf: (f: PickerFolder) => f.parentKey,
}

/**
 * Dokumente nach Ordnern gruppiert, in Baumreihenfolge (Eltern vor Kindern)
 * mit Tiefe und Vorfahren. Ordner ohne Dokumente im Teilbaum entfallen.
 * Dokumente ohne passenden Ordner landen in „Sonstiges" bzw. – wenn die
 * Akte keinen solchen Ordner kennt – in „Weitere Dokumente".
 */
export function groupDocumentsByFolder<T extends PickerDocument> (docs: readonly T[], folders: readonly PickerFolder[]): PickerGroup<T>[] {
  if (folders.length === 0) {
    return docs.length === 0
      ? []
      : [{ key: ORPHAN_GROUP_KEY, label: SINGLE_GROUP_LABEL, path: SINGLE_GROUP_LABEL, depth: 0, parentKey: null, ancestorKeys: [], docs: [...docs], subtreeCount: docs.length }]
  }
  const known = new Set(folders.map(f => f.key))
  const byFolder = new Map<string, T[]>()
  const orphans: T[] = []
  for (const doc of docs) {
    const key = folderKeyForDocument(doc, known)
    if (!known.has(key)) {
      orphans.push(doc)
      continue
    }
    const list = byFolder.get(key) ?? []
    list.push(doc)
    byFolder.set(key, list)
  }

  const subtreeCounts = new Map<string, number>()
  const countSubtree = (node: TreeNode<PickerFolder>): number => {
    const total = (byFolder.get(node.key)?.length ?? 0) + node.children.reduce((sum, c) => sum + countSubtree(c), 0)
    subtreeCounts.set(node.key, total)
    return total
  }
  const roots = buildTree([...folders], FOLDER_TREE)
  for (const root of roots) {
    countSubtree(root)
  }
  const nodes = flattenTree(roots)

  const groups: PickerGroup<T>[] = []
  for (const node of nodes) {
    const subtreeCount = subtreeCounts.get(node.key) ?? 0
    if (subtreeCount === 0) {
      continue
    }
    groups.push({
      key: node.key,
      label: node.item.label,
      path: node.item.path || node.item.label,
      depth: node.depth,
      parentKey: node.parentKey,
      ancestorKeys: node.ancestorKeys,
      docs: byFolder.get(node.key) ?? [],
      subtreeCount,
    })
  }
  if (orphans.length > 0) {
    groups.push({
      key: ORPHAN_GROUP_KEY, label: ORPHAN_GROUP_LABEL, path: ORPHAN_GROUP_LABEL, depth: 0,
      parentKey: null, ancestorKeys: [], docs: orphans, subtreeCount: orphans.length,
    })
  }
  return groups
}
